import React, { useEffect } from "react";
import { useRouteMatch } from "react-router-dom";

// @material-ui/core components
import { CircularProgress, Typography, Box } from "@material-ui/core";
import { CloudUpload, Create, DoneAll, Error } from "@material-ui/icons";

// core components
import CreateCourseCard from "views/Components/CreateCourseCard";
import CreateProjectModal from "views/Dashboard/ProjectsDashboard/CreateProjectModal";

// context and APIs
import NotificationContext from "views/Components/Context.js";
import {
  createProjectAPI,
  fetchAllProjectsAPI,
  deleteProjectAPI,
  updateProjectTagsAPI,
  cloneProjectAPI,
  inviteUserToProjectAPI,
} from "views/APIs/ProjectsUtility";
import AssetDashboard from "views/Components/AssetDashboard";

// styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles({
  ...styles,
});

export default function ProjectsDesignDashboard() {
  // Import images from assets
  const images = require.context("assets/img/create_cards_16_9/", true);
  const path = useRouteMatch().path;
  const classes = useStyles();

  // Context and state variables
  const { showNotification } = React.useContext(NotificationContext);

  // State variables for project creation and management
  const [createProjectModal, setCreateProjectModal] = React.useState(false);
  const [newProjectDetails, setNewProjectDetails] = React.useState({
    name: "",
    description: "",
  });
  const [allProjects, setAllProjects] = React.useState([]);
  const [initialProjects, setInitialProjects] = React.useState([]);
  const [loading, setLoading] = React.useState({ loading: false, message: "" });
  const [projectCreationLoader, setProjectCreationLoader] = React.useState(
    false
  );
  const [projectAdded, setProjectAdded] = React.useState(false);

  // Handle project deletion
  const handleDeleteProject = async (projectData) => {
    const formData = new FormData();
    formData.append("project_id", projectData._id);
    try {
      await deleteProjectAPI(formData);
      setAllProjects(
        allProjects.filter((project) => project._id !== projectData._id)
      );
      showNotification("Project deleted successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  // Handle project cloning
  const handleCloneProject = async (projectData) => {
    try {
      setLoading({ loading: true, message: "Cloning Project. This might take a few minutes depending on the number of artifacts..." });
      const formData = new FormData();
      formData.append("project_id", projectData._id);
      const data = await cloneProjectAPI(formData);
      setAllProjects((prevProjects) => [
        ...prevProjects,
        _mapResourceToAppType(data),
      ]);
      setProjectAdded(true);
      showNotification("Project cloned successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      setLoading({ loading: false, message: "" });
    }
  };

  useEffect(() => {
    const fetchAllProjects = async () => {
      try {
        setLoading({ loading: true, message: "Loading Projects..." });
        const data = await fetchAllProjectsAPI();
        const mappedData = data.map(_mapResourceToAppType);

        setAllProjects(mappedData);
        setInitialProjects(mappedData);
      } catch (error) {
        showNotification(error, "danger");
      } finally {
        setLoading({ loading: false, message: "" });
      }
    };

    fetchAllProjects();
  }, []);

  // Handle project creation
  const handleCreateProject = async () => {
    setProjectCreationLoader(true);

    try {
      // Prepare form data
      const formData = new FormData();
      formData.append("project_name", newProjectDetails.name);
      formData.append("project_description", newProjectDetails.description);

      // Create project via API call
      const data = await createProjectAPI(formData);
      setAllProjects((prevProjects) => [
        ...prevProjects,
        _mapResourceToAppType(data),
      ]);
      setProjectAdded(true);
      showNotification("Project created successfully", "success");
      window.open(`/create/project-design/projects/${data._id}`, "_blank");
    } catch (error) {
      showNotification(
        `Failed to create project: ${error.message || error}`,
        "danger"
      );
    } finally {
      setProjectCreationLoader(false);
      handleCreateProjectModalClose();
    }
  };

  const handleSaveLabel = async (data) => {
    try {
      const formData = new FormData();
      formData.append("project_id", data._id);
      if (data.tags.length === 0) {
        formData.append("tags", []);
      }
      // Append each tag separately
      else {
        data.tags.forEach((tag) => formData.append("tags", tag));
      }
      await updateProjectTagsAPI(formData);
      setAllProjects((prevProjects) =>
        prevProjects.map((project) =>
          project._id === data._id ? { ...project, tags: data.tags } : project
        )
      );
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  const handleInviteSend = async (data) => {
    try {
      const formData = new FormData();
      formData.append("project_id", data._id);
      formData.append("email", data.email);

      const response = await inviteUserToProjectAPI(formData);
      const updatedProjectId = response.project_id;

      // Find the target asset
      const updatedAsset = allProjects.find(
        (asset) => asset._id === updatedProjectId
      );
      if (!updatedAsset) {
        console.warn(
          "⚠️ Could not find the matching asset for ID:",
          updatedProjectId
        );
        return;
      }

      // Create a new invitee object
      const newInvitee = {
        invitee_name: response.name,
        email: response.email,
        status: "pending",
      };
      // Update or append the invitee
      const updatedInvitees = (updatedAsset.invitees || []).map((invitee) =>
        invitee.email === newInvitee.email
          ? { ...invitee, status: "pending" } // Update status if email matches
          : invitee
      );

      // If no match was found in map, add the new invitee
      const emailExists = updatedInvitees.some(
        (inv) => inv.email === newInvitee.email
      );
      if (!emailExists) {
        updatedInvitees.push(newInvitee);
      }

      const newAsset = {
        ...updatedAsset,
        invitees: updatedInvitees,
      };

      // Update the allProjects state
      const updatedProjects = allProjects.map((project) =>
        project._id === updatedProjectId ? newAsset : project
      );

      setAllProjects(updatedProjects);
      showNotification("Invite sent successfully", "success");
    } catch (error) {
      console.error("Error sending invite:", error);
      showNotification(error?.message || "Failed to send invite", "danger");
    }
  };

  // Close project creation modal and reset state
  const handleCreateProjectModalClose = () => {
    setCreateProjectModal(false);
    setNewProjectDetails({
      name: "",
      description: "",
    });
  };

  // Get status icon based on project status
  const getStatusIcon = (status) => {
    switch (status) {
      case "In Design Phase":
        return <Create style={{ color: "#435966" }} />;
      case "Ready for Publishing":
        return <CloudUpload style={{ color: "#435966" }} />;
      case "Published":
        return <DoneAll style={{ color: "#435966" }} />;
      case "Publishing Failed":
        return <Error style={{ color: "#435966" }} />;
      default:
        return null;
    }
  };

  const _mapResourceToAppType = (resource) => {
    // Destructure the resource to exclude project_name and project_description
    const { name, description, ...rest } = resource;

    return {
      ...rest, // Spread all remaining properties
      name: name, // Add name property
      description: description, // Add description property
    };
  };

  // Show loading spinner while fetching projects
  if (loading.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress
          color="quCreateColor"
          className={classes.loaderClass}
        />
        <Typography
          variant="h6"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          {loading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <CreateProjectModal
        newProjectDetails={newProjectDetails}
        setNewProjectDetails={setNewProjectDetails}
        createProjectModal={createProjectModal}
        handleCreateProjectModalClose={handleCreateProjectModalClose}
        handleCreateProject={handleCreateProject}
        projectCreationLoader={projectCreationLoader}
      />

      <AssetDashboard
        path={path}
        welcomeTitle="Project Design Dashboard"
        welcomeSubtitle="Start by creating a new project or reviewing your existing projects."
        allAssets={allProjects}
        setAllAssets={setAllProjects}
        initialAssets={initialProjects}
        noAssetMessage="Create or Review your projects to get started."
        linkPath="/projects/"
        getStatusIcon={getStatusIcon}
        CreateAssetCard={CreateCourseCard}
        setCreateAssetModal={setCreateProjectModal}
        createAssetButtonText="Create Project"
        handleSaveLabel={handleSaveLabel}
        handleInviteSend={handleInviteSend}
        asset_identifier="project"
        handleDeleteAsset={handleDeleteProject}
        handleCloneAsset={handleCloneProject}
        assetAdded={projectAdded}
        setAssetAdded={setProjectAdded}
        setInitialAssets={setInitialProjects}
        workflowConfig={{
          type: "project",
          title: "My Projects",
          description: `Use the projects dashboard to create, manage, and organize all of your artifacts under a single project.`,
          videoUrl:
            "https://qucoursify.s3.us-east-1.amazonaws.com/qcreate/demos/projects-dashboard.mp4",
          steps: [
            {
              label: "1. Setup project",
              body:
                "Projects serve as containers for labs, courses, writing artifacts, podcasts, models, and templates.",
            },
            {
              label: "2. Create/Import artifacts",
              body:
                "You can create new artifacts directly inside a project or import existing ones.",
            },
            {
              label: "3. Invite users",
              body: "Invite users to your project to collaborate on artifacts.",
            },
          ],
        }}
      />
    </>
  );
}
